import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";
import { loadStripe, type Stripe } from "@stripe/stripe-js";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";
import { Button, LoadingSkeleton } from "@bloom/ui";
import { useLocale } from "../locale/locale-context.tsx";
import { useAuth } from "../auth/auth-context.tsx";
import { api } from "../api/client.ts";

let stripePromise: Promise<Stripe | null> | null = null;
function getStripe() {
  if (!stripePromise) {
    const key = (window as { __BLOOM_STRIPE_PK?: string }).__BLOOM_STRIPE_PK ?? "";
    stripePromise = loadStripe(key);
  }
  return stripePromise;
}

export function UnlockPage() {
  const { kind = "", targetId = "" } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLocale();
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) {
      navigate("/login?next=" + encodeURIComponent(window.location.pathname));
      return;
    }
    const body =
      kind === "pack"
        ? {
            kind: "pack" as const,
            seriesId: targetId,
            startEpisodeId: params.get("startEpisodeId"),
            count: Number(params.get("count") ?? 2),
          }
        : { kind: "bundle" as const, seriesId: targetId };
    void (async () => {
      try {
        const data = await api.post<{ clientSecret: string }>("/api/payments/checkout", body);
        setClientSecret(data.clientSecret);
      } catch (err) {
        setError((err as Error).message);
      }
    })();
  }, [kind, targetId, params, user, navigate]);

  if (error) {
    return (
      <div className="p-6 text-center">
        <p className="text-[var(--brand-rose)]">{error}</p>
        <Button variant="ghost" onClick={() => history.back()}>
          {t("auth.continue")}
        </Button>
      </div>
    );
  }

  if (!clientSecret) {
    return (
      <div className="mx-auto max-w-md p-6">
        <LoadingSkeleton className="h-96 w-full" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl p-6">
      <h1 className="mb-4 font-heading text-2xl font-bold">{t("checkout.title")}</h1>
      <EmbeddedCheckoutProvider stripe={getStripe()} options={{ clientSecret }}>
        <EmbeddedCheckout />
      </EmbeddedCheckoutProvider>
    </div>
  );
}
